import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FaArrowDown,
  FaArrowUp,
  FaBoxes,
  FaCheckCircle,
  FaClipboardList,
  FaDownload,
  FaEdit,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaWarehouse,
} from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import Modal from '../components/Modal';
import InventoryService from '../services/InventoryService';
import {
  INVENTORY_MANAGE_PERMISSION,
  INVENTORY_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';

const MOVEMENT_TYPES = [
  { value: 'INBOUND', label: 'Entrada', icon: FaArrowDown },
  { value: 'OUTBOUND', label: 'Saída', icon: FaArrowUp },
  { value: 'TRANSFER', label: 'Transferência', icon: FaExchangeAlt },
];

const resolveErrorMessage = (error) => {
  if (!error) return 'Ocorreu um erro inesperado.';
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (typeof error.message === 'string') {
    return error.message;
  }
  return 'Ocorreu um erro inesperado.';
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
  }).format(date);
};

const toInputDateValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const emptyItemForm = {
  id: null,
  name: '',
  sku: '',
  categoryId: '',
  warehouseId: '',
  unit: 'un',
  costPrice: '',
  salePrice: '',
  minimumStock: '',
  maximumStock: '',
  safetyStock: '',
  description: '',
  trackBatch: false,
  trackExpiration: false,
  expirationDate: '',
  active: true,
};

const emptyWarehouseForm = {
  id: null,
  name: '',
  code: '',
  manager: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
};

const emptyCategoryForm = {
  id: null,
  name: '',
  description: '',
};

const emptyMovementForm = {
  itemId: '',
  type: 'INBOUND',
  quantity: '',
  originWarehouseId: '',
  targetWarehouseId: '',
  unitCost: '',
  occurredAt: new Date().toISOString().slice(0, 16),
  referenceCode: '',
  notes: '',
};

const normalizeCategory = (category) => {
  if (!category || typeof category !== 'object') {
    return { id: null, name: 'Sem categoria', description: '' };
  }
  return {
    id: category.id ?? category.categoryId ?? null,
    name: category.name ?? category.title ?? 'Sem categoria',
    description: category.description ?? category.notes ?? '',
  };
};

const normalizeWarehouse = (warehouse) => {
  if (!warehouse || typeof warehouse !== 'object') {
    return {
      id: null,
      name: 'Geral',
      code: '',
      manager: '',
      phone: '',
      email: '',
      address: '',
      notes: '',
    };
  }
  return {
    id: warehouse.id ?? warehouse.warehouseId ?? null,
    name: warehouse.name ?? warehouse.title ?? 'Geral',
    code: warehouse.code ?? warehouse.identifier ?? '',
    manager: warehouse.manager ?? warehouse.responsible ?? '',
    phone: warehouse.phone ?? warehouse.contactPhone ?? '',
    email: warehouse.email ?? warehouse.contactEmail ?? '',
    address: warehouse.address ?? warehouse.location ?? '',
    notes: warehouse.notes ?? warehouse.description ?? '',
  };
};

const normalizeItem = (item) => {
  if (!item || typeof item !== 'object') {
    return {
      id: null,
      name: 'Item sem nome',
      sku: '',
      category: normalizeCategory(null),
      warehouse: normalizeWarehouse(null),
      warehouses: [],
      unit: 'un',
      stock: 0,
      reserved: 0,
      minimumStock: 0,
      maximumStock: 0,
      safetyStock: 0,
      costPrice: 0,
      salePrice: 0,
      active: true,
      updatedAt: null,
      lastMovementAt: null,
      expirationDate: null,
    };
  }

  const category = normalizeCategory(item.category ?? {
    id: item.categoryId,
    name: item.categoryName,
    description: item.categoryDescription,
  });

  const warehouse = normalizeWarehouse(item.warehouse ?? {
    id: item.warehouseId,
    name: item.warehouseName,
    code: item.warehouseCode,
  });

  const warehouseEntries = Array.isArray(item.warehouseStocks)
    ? item.warehouseStocks.map((stock) => {
        const normalized = normalizeWarehouse(stock.warehouse ?? {
          id: stock.warehouseId ?? stock.id ?? null,
          name: stock.warehouseName,
          code: stock.warehouseCode,
        });
        return {
          ...normalized,
          quantity: Number(stock.quantity ?? stock.amount ?? stock.stock ?? 0),
        };
      })
    : [];

  const primaryWarehouse =
    warehouseEntries.find((entry) => entry.id === warehouse.id) ||
    warehouseEntries.find((entry) => entry.quantity > 0) ||
    warehouseEntries[0] ||
    warehouse;

  const stockFromEntries = warehouseEntries.reduce(
    (total, entry) => total + Number(entry.quantity || 0),
    0,
  );
  const stockFallback = Number(
    item.stock ?? item.availableQuantity ?? item.currentQuantity ?? 0,
  );
  const stock =
    warehouseEntries.length > 0 && Number.isFinite(stockFromEntries)
      ? stockFromEntries
      : stockFallback;
  const reserved = Number(item.reserved ?? item.reservedQuantity ?? 0);
  const minimumStock = Number(
    item.minimumStock ?? item.reorderPoint ?? item.minimumQuantity ?? 0,
  );
  const maximumStock = Number(item.maximumStock ?? item.maximumQuantity ?? 0);
  const safetyStock = Number(item.safetyStock ?? item.bufferQuantity ?? 0);

  return {
    id: item.id ?? item.itemId ?? null,
    name: item.name ?? item.title ?? 'Item sem nome',
    sku: item.sku ?? item.code ?? item.reference ?? '',
    category,
    warehouse: primaryWarehouse,
    warehouses: warehouseEntries,
    unit: item.unit ?? item.measureUnit ?? 'un',
    stock,
    reserved,
    minimumStock,
    maximumStock,
    safetyStock,
    costPrice: Number(item.costPrice ?? item.averageCost ?? 0),
    salePrice: Number(item.salePrice ?? item.price ?? 0),
    active: item.active ?? item.enabled ?? true,
    lastMovementAt: item.lastMovementAt ?? item.lastMovementDate ?? null,
    updatedAt: item.updatedAt ?? item.updatedAt ?? null,
    trackBatch: Boolean(item.trackBatch ?? item.batchControl ?? false),
    trackExpiration: Boolean(
      item.trackExpiration ?? item.expirationControl ?? false,
    ),
    description: item.description ?? item.notes ?? '',
    expirationDate: item.expirationDate ?? item.validUntil ?? null,
  };
};

const normalizeMovement = (movement) => {
  if (!movement || typeof movement !== 'object') {
    return {
      id: null,
      type: 'INBOUND',
      quantity: 0,
      unitCost: 0,
      occurredAt: null,
      referenceCode: '',
      notes: '',
      item: normalizeItem(null),
      originWarehouse: normalizeWarehouse(null),
      targetWarehouse: normalizeWarehouse(null),
    };
  }

  return {
    id: movement.id ?? movement.movementId ?? null,
    type: movement.type ?? movement.movementType ?? 'INBOUND',
    quantity: Number(movement.quantity ?? movement.amount ?? 0),
    unitCost: Number(movement.unitCost ?? movement.cost ?? 0),
    occurredAt: movement.occurredAt ?? movement.date ?? null,
    referenceCode: movement.referenceCode ?? movement.reference ?? '',
    notes: movement.notes ?? movement.description ?? '',
    item: normalizeItem(movement.item ?? movement.inventoryItem ?? null),
    originWarehouse: normalizeWarehouse(
      movement.originWarehouse ?? movement.fromWarehouse ?? null,
    ),
    targetWarehouse: normalizeWarehouse(
      movement.targetWarehouse ?? movement.toWarehouse ?? null,
    ),
  };
};

const buildItemPayload = (form) => ({
  name: form.name.trim(),
  sku: form.sku.trim() || null,
  categoryId:
    form.categoryId && Number.isFinite(Number(form.categoryId))
      ? Number(form.categoryId)
      : null,
  warehouseId:
    form.warehouseId && Number.isFinite(Number(form.warehouseId))
      ? Number(form.warehouseId)
      : null,
  unit: form.unit.trim() || 'un',
  costPrice: form.costPrice ? Number(form.costPrice) : null,
  salePrice: form.salePrice ? Number(form.salePrice) : null,
  minimumStock: form.minimumStock ? Number(form.minimumStock) : null,
  maximumStock: form.maximumStock ? Number(form.maximumStock) : null,
  safetyStock: form.safetyStock ? Number(form.safetyStock) : null,
  description: form.description?.trim() || null,
  trackBatch: Boolean(form.trackBatch),
  trackExpiration: Boolean(form.trackExpiration),
  expirationDate: form.expirationDate
    ? new Date(`${form.expirationDate}T00:00:00`).toISOString()
    : null,
  active: Boolean(form.active),
});

const buildWarehousePayload = (form) => ({
  name: form.name.trim(),
  code: form.code.trim() || null,
  manager: form.manager.trim() || null,
  phone: form.phone.trim() || null,
  email: form.email.trim() || null,
  address: form.address.trim() || null,
  notes: form.notes.trim() || null,
});

const buildCategoryPayload = (form) => ({
  name: form.name.trim(),
  description: form.description.trim() || null,
});

const buildMovementPayload = (form) => ({
  itemId:
    form.itemId && Number.isFinite(Number(form.itemId))
      ? Number(form.itemId)
      : null,
  type: form.type,
  quantity: Number(form.quantity || 0),
  originWarehouseId:
    form.type === 'INBOUND'
      ? null
      : form.originWarehouseId && Number.isFinite(Number(form.originWarehouseId))
      ? Number(form.originWarehouseId)
      : null,
  targetWarehouseId:
    form.type === 'OUTBOUND'
      ? null
      : form.targetWarehouseId && Number.isFinite(Number(form.targetWarehouseId))
      ? Number(form.targetWarehouseId)
      : null,
  unitCost: form.unitCost ? Number(form.unitCost) : null,
  occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : null,
  referenceCode: form.referenceCode?.trim() || null,
  notes: form.notes?.trim() || null,
});

export default function Inventory() {
  const { user } = useAuth();
  const canManage = hasPermission(user, INVENTORY_MANAGE_PERMISSION);
  const canView =
    canManage || hasPermission(user, INVENTORY_VIEW_PERMISSION);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [movements, setMovements] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState('success');
  const [reportFormat, setReportFormat] = useState('pdf');

  const [filters, setFilters] = useState({
    search: '',
    warehouseId: 'all',
    categoryId: 'all',
    status: 'all',
  });

  const [movementFilters, setMovementFilters] = useState({
    type: 'all',
    warehouseId: 'all',
    period: '30',
  });

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [savingItem, setSavingItem] = useState(false);

  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementForm, setMovementForm] = useState(emptyMovementForm);
  const [savingMovement, setSavingMovement] = useState(false);

  const [warehouseModalOpen, setWarehouseModalOpen] = useState(false);
  const [warehouseForm, setWarehouseForm] = useState(emptyWarehouseForm);
  const [savingWarehouse, setSavingWarehouse] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [savingCategory, setSavingCategory] = useState(false);

  const showFeedback = useCallback((message, type = 'success') => {
    setFeedback(message);
    setFeedbackType(type);
    if (message) {
      setTimeout(() => {
        setFeedback('');
      }, 6000);
    }
  }, []);

  const handleError = useCallback((message) => {
    setError(message);
    if (message) {
      setTimeout(() => setError(''), 8000);
    }
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const response = await InventoryService.listItems({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setItems(data.map(normalizeItem));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setItems([]);
    }
  }, [handleError]);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await InventoryService.listWarehouses({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setWarehouses(data.map(normalizeWarehouse));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setWarehouses([]);
    }
  }, [handleError]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await InventoryService.listCategories({});
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setCategories(data.map(normalizeCategory));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setCategories([]);
    }
  }, [handleError]);

  const loadMovements = useCallback(async () => {
    try {
      const response = await InventoryService.listMovements({ limit: 100 });
      const data = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
        ? response.data
        : [];
      setMovements(data.map(normalizeMovement));
    } catch (err) {
      handleError(resolveErrorMessage(err));
      setMovements([]);
    }
  }, [handleError]);

  const loadAll = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    await Promise.allSettled([
      loadItems(),
      loadWarehouses(),
      loadCategories(),
      loadMovements(),
    ]);
    setLoading(false);
  }, [canView, loadItems, loadWarehouses, loadCategories, loadMovements]);

  useEffect(() => {
    if (!canView) return;
    loadAll();
  }, [canView, loadAll]);

  const lowStockItems = useMemo(
    () =>
      items.filter((item) =>
        Number.isFinite(item.minimumStock)
          ? item.stock <= item.minimumStock
          : false,
      ),
    [items],
  );

  const totalInventoryValue = useMemo(
    () =>
      items.reduce((total, item) => {
        const stock = Number(item.stock || 0);
        const cost = Number(item.costPrice || 0);
        return total + stock * cost;
      }, 0),
    [items],
  );

  const recentOutboundValue = useMemo(() => {
    const now = Date.now();
    const limitDate =
      movementFilters.period === '0'
        ? null
        : now - Number(movementFilters.period || 30) * 24 * 60 * 60 * 1000;
    return movements
      .filter((movement) => {
        if (movement.type !== 'OUTBOUND') return false;
        if (!limitDate) return true;
        const date = movement.occurredAt
          ? new Date(movement.occurredAt).getTime()
          : 0;
        return Number.isFinite(date) && date >= limitDate;
      })
      .reduce((total, movement) => {
        const quantity = Number(movement.quantity || 0);
        const unitCost = Number(movement.unitCost || 0);
        return total + quantity * unitCost;
      }, 0);
  }, [movements, movementFilters.period]);

  const filteredItems = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return items.filter((item) => {
      const matchesTerm = term
        ? [
            item.name,
            item.sku,
            item.category?.name,
            item.warehouse?.name,
            ...(Array.isArray(item.warehouses)
              ? item.warehouses.map((warehouse) => warehouse.name)
              : []),
          ]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term))
        : true;

      const matchesWarehouse =
        filters.warehouseId === 'all' ||
        [
          item.warehouse?.id,
          ...(Array.isArray(item.warehouses)
            ? item.warehouses.map((warehouse) => warehouse.id)
            : []),
        ]
          .filter((id) => id !== undefined && id !== null)
          .some((id) => String(id ?? '') === String(filters.warehouseId));

      const matchesCategory =
        filters.categoryId === 'all' ||
        String(item.category?.id ?? '') === String(filters.categoryId);

      const status = (() => {
        if (!item.active) return 'inactive';
        if (item.minimumStock && item.stock <= item.minimumStock) return 'low';
        if (item.maximumStock && item.stock > item.maximumStock) return 'excess';
        return 'ok';
      })();

      const matchesStatus = filters.status === 'all' || status === filters.status;

      return matchesTerm && matchesWarehouse && matchesCategory && matchesStatus;
    });
  }, [items, filters]);

  const selectedMovementItem = useMemo(() => {
    if (!movementForm.itemId) return null;
    return items.find(
      (candidate) => String(candidate.id ?? '') === String(movementForm.itemId),
    );
  }, [items, movementForm.itemId]);

  const filteredMovements = useMemo(() => {
    const now = Date.now();
    const periodDays = Number(movementFilters.period);
    const limitDate =
      movementFilters.period === '0'
        ? null
        : Number.isFinite(periodDays)
        ? now - periodDays * 24 * 60 * 60 * 1000
        : null;

    return movements.filter((movement) => {
      const matchesType =
        movementFilters.type === 'all' || movement.type === movementFilters.type;

      const matchesWarehouse = (() => {
        if (movementFilters.warehouseId === 'all') return true;
        const warehouseId = String(movementFilters.warehouseId);
        return (
          String(movement.originWarehouse?.id ?? '') === warehouseId ||
          String(movement.targetWarehouse?.id ?? '') === warehouseId
        );
      })();

      const matchesPeriod = (() => {
        if (!limitDate) return true;
        const movementDate = movement.occurredAt
          ? new Date(movement.occurredAt).getTime()
          : null;
        return Number.isFinite(movementDate) ? movementDate >= limitDate : false;
      })();

      return matchesType && matchesWarehouse && matchesPeriod;
    });
  }, [movements, movementFilters]);

  const toItemForm = useCallback((item) => ({
    id: item?.id ?? null,
    name: item?.name ?? '',
    sku: item?.sku ?? '',
    categoryId: item?.category?.id ?? '',
    warehouseId: item?.warehouse?.id ?? '',
    unit: item?.unit ?? 'un',
    costPrice: item?.costPrice ? String(item.costPrice) : '',
    salePrice: item?.salePrice ? String(item.salePrice) : '',
    minimumStock: item?.minimumStock ? String(item.minimumStock) : '',
    maximumStock: item?.maximumStock ? String(item.maximumStock) : '',
    safetyStock: item?.safetyStock ? String(item.safetyStock) : '',
    description: item?.description ?? '',
    trackBatch: Boolean(item?.trackBatch),
    trackExpiration: Boolean(item?.trackExpiration),
    expirationDate: toInputDateValue(item?.expirationDate),
    active: Boolean(item?.active ?? true),
  }), []);

  const toWarehouseForm = useCallback((warehouse) => ({
    id: warehouse?.id ?? null,
    name: warehouse?.name ?? '',
    code: warehouse?.code ?? '',
    manager: warehouse?.manager ?? '',
    phone: warehouse?.phone ?? '',
    email: warehouse?.email ?? '',
    address: warehouse?.address ?? '',
    notes: warehouse?.notes ?? '',
  }), []);

  const toCategoryForm = useCallback((category) => ({
    id: category?.id ?? null,
    name: category?.name ?? '',
    description: category?.description ?? '',
  }), []);

  const openItemModal = useCallback(
    (item = null) => {
      setItemForm(toItemForm(item));
      setItemModalOpen(true);
    },
    [toItemForm],
  );

  const openWarehouseModal = useCallback(
    (warehouse = null) => {
      setWarehouseForm(toWarehouseForm(warehouse));
      setWarehouseModalOpen(true);
    },
    [toWarehouseForm],
  );

  const openCategoryModal = useCallback(
    (category = null) => {
      setCategoryForm(toCategoryForm(category));
      setCategoryModalOpen(true);
    },
    [toCategoryForm],
  );

  const openMovementModal = useCallback(
    (item = null) => {
      const preferredWarehouse = item?.warehouses?.find(
        (warehouse) => Number(warehouse.quantity || 0) > 0,
      );
      const defaultWarehouseId =
        preferredWarehouse?.id ?? item?.warehouse?.id ?? '';
      const defaultWarehouseValue =
        defaultWarehouseId !== '' ? String(defaultWarehouseId) : '';
      setMovementForm({
        ...emptyMovementForm,
        itemId: item?.id ? String(item.id) : '',
        originWarehouseId: defaultWarehouseValue,
        targetWarehouseId: defaultWarehouseValue,
      });
      setMovementModalOpen(true);
    },
    [],
  );

  const handleDeleteItem = useCallback(
    async (item) => {
      if (!canManage || !item?.id) return;
      const confirmed = window.confirm(
        `Excluir o item "${item.name}"? Essa ação não pode ser desfeita.`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteItem(item.id);
        showFeedback('Item excluído com sucesso.');
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadItems, showFeedback],
  );

  const handleDeleteWarehouse = useCallback(
    async (warehouse) => {
      if (!canManage || !warehouse?.id) return;
      const confirmed = window.confirm(
        `Excluir o depósito "${warehouse.name}"?`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteWarehouse(warehouse.id);
        showFeedback('Depósito excluído com sucesso.');
        loadWarehouses();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadItems, loadWarehouses, showFeedback],
  );

  const handleDeleteCategory = useCallback(
    async (category) => {
      if (!canManage || !category?.id) return;
      const confirmed = window.confirm(
        `Excluir a categoria "${category.name}"?`,
      );
      if (!confirmed) return;
      try {
        await InventoryService.deleteCategory(category.id);
        showFeedback('Categoria excluída com sucesso.');
        loadCategories();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      }
    },
    [canManage, handleError, loadCategories, loadItems, showFeedback],
  );

  const handleSaveItem = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingItem(true);
      try {
        const payload = buildItemPayload(itemForm);
        if (itemForm.id) {
          await InventoryService.updateItem(itemForm.id, payload);
          showFeedback('Item atualizado com sucesso.');
        } else {
          await InventoryService.createItem(payload);
          showFeedback('Item cadastrado com sucesso.');
        }
        setItemModalOpen(false);
        setItemForm({ ...emptyItemForm });
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingItem(false);
      }
    },
    [canManage, handleError, itemForm, loadItems, showFeedback],
  );

  const handleSaveWarehouse = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingWarehouse(true);
      try {
        const payload = buildWarehousePayload(warehouseForm);
        if (warehouseForm.id) {
          await InventoryService.updateWarehouse(warehouseForm.id, payload);
          showFeedback('Depósito atualizado com sucesso.');
        } else {
          await InventoryService.createWarehouse(payload);
          showFeedback('Depósito cadastrado com sucesso.');
        }
        setWarehouseModalOpen(false);
        setWarehouseForm({ ...emptyWarehouseForm });
        loadWarehouses();
        loadItems();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingWarehouse(false);
      }
    },
    [canManage, handleError, loadItems, loadWarehouses, showFeedback, warehouseForm],
  );

  const handleSaveCategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingCategory(true);
      try {
        const payload = buildCategoryPayload(categoryForm);
        if (categoryForm.id) {
          await InventoryService.updateCategory(categoryForm.id, payload);
          showFeedback('Categoria atualizada com sucesso.');
        } else {
          await InventoryService.createCategory(payload);
          showFeedback('Categoria cadastrada com sucesso.');
        }
        setCategoryModalOpen(false);
        setCategoryForm({ ...emptyCategoryForm });
        loadCategories();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingCategory(false);
      }
    },
    [canManage, categoryForm, handleError, loadCategories, showFeedback],
  );

  const handleSaveMovement = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      setSavingMovement(true);
      try {
        const payload = buildMovementPayload(movementForm);
        await InventoryService.registerMovement(payload);
        showFeedback('Movimentação registrada com sucesso.');
        setMovementModalOpen(false);
        setMovementForm({ ...emptyMovementForm });
        loadItems();
        loadMovements();
      } catch (err) {
        handleError(resolveErrorMessage(err));
      } finally {
        setSavingMovement(false);
      }
    },
    [canManage, handleError, loadItems, loadMovements, movementForm, showFeedback],
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      const response = await InventoryService.downloadReport({
        warehouseId:
          filters.warehouseId !== 'all' ? filters.warehouseId : undefined,
        categoryId:
          filters.categoryId !== 'all' ? filters.categoryId : undefined,
        format: reportFormat,
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(
        /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
      );
      const resolvedName = match?.[1] || match?.[2];
      const fallbackExtension =
        reportFormat === 'pdf' ? 'pdf' : reportFormat === 'xlsx' ? 'xlsx' : 'xls';
      const fileName = resolvedName
        ? decodeURIComponent(resolvedName.replace(/"/g, ''))
        : `relatorio-estoque.${fallbackExtension}`;

      const contentType =
        response.headers?.['content-type'] ||
        (reportFormat === 'pdf'
          ? 'application/pdf'
          : reportFormat === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/vnd.ms-excel');

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleError(resolveErrorMessage(err));
    }
  }, [filters.categoryId, filters.warehouseId, handleError, reportFormat]);

  if (!canView) {
    return (
      <div className="p-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-700">
          <div className="flex items-center gap-3 text-lg font-semibold">
            <FaExclamationTriangle />
            <span>Acesso restrito</span>
          </div>
          <p className="mt-3 text-sm text-amber-700">
            Você não possui permissão para visualizar o módulo de estoque.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-semibold text-slate-800">
            <FaBoxes className="text-indigo-500" />
            <span>Controle de Estoque</span>
          </h1>
          <p className="text-sm text-slate-500">
            Cadastre produtos, depósitos e acompanhe movimentações em tempo real.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={loadAll}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
          >
            <FaSyncAlt className="text-xs" /> Atualizar
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => openItemModal(null)}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500"
            >
              <FaPlus /> Novo item
            </button>
          )}
        </div>
      </header>

      {(feedback || error) && (
        <div
          className={`rounded-2xl border p-4 text-sm ${
            feedback
              ? feedbackType === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-amber-200 bg-amber-50 text-amber-700'
              : 'border-rose-200 bg-rose-50 text-rose-700'
          }`}
        >
          <div className="flex items-center gap-2 font-medium">
            {feedback ? <FaCheckCircle /> : <FaExclamationTriangle />}
            <span>{feedback || error}</span>
          </div>
        </div>
      )}

      <section>
        <h2 className="text-lg font-semibold text-slate-700">Visão geral</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-slate-500">Itens cadastrados</p>
            <p className="mt-2 text-2xl font-semibold text-slate-800">
              {items.length}
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
              <FaExclamationTriangle /> Itens em alerta
            </p>
            <p className="mt-2 text-2xl font-semibold text-amber-700">
              {lowStockItems.length}
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <p className="text-sm font-medium text-emerald-700">Valor total</p>
            <p className="mt-2 text-2xl font-semibold text-emerald-700">
              {formatCurrency(totalInventoryValue)}
            </p>
          </div>
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
            <p className="text-sm font-medium text-indigo-700">Saídas (último período)</p>
            <p className="mt-2 text-2xl font-semibold text-indigo-700">
              {formatCurrency(recentOutboundValue)}
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="text-lg font-semibold text-slate-700">Itens em estoque</h2>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={filters.search}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, search: event.target.value }))
                }
                placeholder="Buscar por nome, SKU ou categoria"
                className="w-72 rounded-full border border-slate-200 py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </div>
            <select
              value={filters.warehouseId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, warehouseId: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todos os depósitos</option>
              {warehouses.map((warehouse) => (
                <option
                  key={warehouse.id ?? warehouse.name}
                  value={warehouse.id ?? warehouse.name}
                >
                  {warehouse.name}
                </option>
              ))}
            </select>
            <select
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, categoryId: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todas as categorias</option>
              {categories.map((category) => (
                <option
                  key={category.id ?? category.name}
                  value={category.id ?? category.name}
                >
                  {category.name}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, status: event.target.value }))
              }
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="all">Todos os status</option>
              <option value="ok">Saudável</option>
              <option value="low">Abaixo do mínimo</option>
              <option value="excess">Acima do máximo</option>
              <option value="inactive">Inativo</option>
            </select>
            <select
              value={reportFormat}
              onChange={(event) => setReportFormat(event.target.value)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
            >
              <option value="pdf">PDF</option>
              <option value="xlsx">Excel (.xlsx)</option>
              <option value="xls">Excel (.xls)</option>
            </select>
            <button
              type="button"
              onClick={handleDownloadReport}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
            >
              <FaDownload /> Exportar
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Produto</th>
                  <th className="px-4 py-3 text-left">Categoria</th>
                  <th className="px-4 py-3 text-left">Depósitos</th>
                  <th className="px-4 py-3 text-right">Estoque</th>
                  <th className="px-4 py-3 text-right">Mínimo</th>
                  <th className="px-4 py-3 text-right">Máximo</th>
                  <th className="px-4 py-3 text-right">Custo</th>
                  <th className="px-4 py-3 text-right">Validade</th>
                  <th className="px-4 py-3 text-right">Atualizado</th>
                  {canManage && <th className="px-4 py-3 text-right">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={canManage ? 9 : 8}
                      className="px-4 py-6 text-center text-slate-500"
                    >
                      Carregando informações de estoque...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canManage ? 9 : 8}
                      className="px-4 py-6 text-center text-slate-500"
                    >
                      Nenhum item encontrado para os filtros selecionados.
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => {
                    const statusBadge = (() => {
                      if (!item.active) {
                        return 'bg-slate-100 text-slate-500';
                      }
                      if (item.minimumStock && item.stock <= item.minimumStock) {
                        return 'bg-amber-100 text-amber-700';
                      }
                      if (item.maximumStock && item.stock > item.maximumStock) {
                        return 'bg-indigo-100 text-indigo-700';
                      }
                      return 'bg-emerald-100 text-emerald-700';
                    })();

                    return (
                      <tr
                        key={item.id ?? item.sku}
                        className="hover:bg-slate-50/60"
                      >
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-slate-800">
                              {item.name}
                            </span>
                            <span className="text-xs text-slate-400">
                              {item.sku || 'Sem SKU'}
                            </span>
                            <span
                              className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusBadge}`}
                            >
                              {item.active
                                ? item.minimumStock && item.stock <= item.minimumStock
                                  ? 'Atenção: abaixo do mínimo'
                                  : item.maximumStock && item.stock > item.maximumStock
                                  ? 'Acima do máximo'
                                  : 'Estoque saudável'
                                : 'Inativo'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {item.category?.name || 'Sem categoria'}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {Array.isArray(item.warehouses) && item.warehouses.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {item.warehouses.map((entry) => (
                                <span
                                  key={`${item.id ?? item.sku}-${entry.id ?? entry.name}`}
                                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                                >
                                  <span className="font-medium">
                                    {entry.name || 'Sem depósito'}
                                  </span>
                                  <span className="text-slate-500">
                                    {Number(entry.quantity || 0)} {item.unit}
                                  </span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">Sem depósito</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-800">
                          {item.stock} {item.unit}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.minimumStock || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.maximumStock || '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600">
                          {formatCurrency(item.costPrice)}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {item.expirationDate
                            ? formatDate(item.expirationDate)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500">
                          {formatDateTime(item.lastMovementAt || item.updatedAt)}
                        </td>
                        {canManage && (
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                                onClick={() => openMovementModal(item)}
                              >
                                <FaExchangeAlt /> Movimentar
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                                onClick={() => openItemModal(item)}
                              >
                                <FaEdit /> Editar
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50"
                                onClick={() => handleDeleteItem(item)}
                              >
                                <FaTrash /> Excluir
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-700">
              <FaClipboardList className="text-indigo-500" /> Movimentações recentes
            </h2>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={movementFilters.type}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    type: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todos os tipos</option>
                {MOVEMENT_TYPES.map((movement) => (
                  <option key={movement.value} value={movement.value}>
                    {movement.label}
                  </option>
                ))}
              </select>
              <select
                value={movementFilters.warehouseId}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    warehouseId: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="all">Todos os depósitos</option>
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.id ?? warehouse.name}
                    value={warehouse.id ?? warehouse.name}
                  >
                    {warehouse.name}
                  </option>
                ))}
              </select>
              <select
                value={movementFilters.period}
                onChange={(event) =>
                  setMovementFilters((prev) => ({
                    ...prev,
                    period: event.target.value,
                  }))
                }
                className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="7">Últimos 7 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
                <option value="365">Últimos 12 meses</option>
                <option value="0">Todo o histórico</option>
              </select>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {filteredMovements.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
                Nenhuma movimentação registrada no período selecionado.
              </div>
            ) : (
              filteredMovements.slice(0, 15).map((movement) => {
                const typeInfo = MOVEMENT_TYPES.find(
                  (type) => type.value === movement.type,
                );
                const TypeIcon = typeInfo?.icon ?? FaExchangeAlt;
                const isInbound = movement.type === 'INBOUND';
                const isOutbound = movement.type === 'OUTBOUND';
                const colorClass = isInbound
                  ? 'text-emerald-600'
                  : isOutbound
                  ? 'text-rose-600'
                  : 'text-indigo-600';

                return (
                  <div
                    key={movement.id ?? `${movement.type}-${movement.referenceCode}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <TypeIcon className={colorClass} />
                        <span>
                          {typeInfo?.label ?? 'Movimentação'} • {movement.item.name}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">
                        {movement.originWarehouse?.name && (
                          <span>
                            Origem: {movement.originWarehouse.name}
                            {' · '}
                          </span>
                        )}
                        {movement.targetWarehouse?.name && (
                          <span>
                            Destino: {movement.targetWarehouse.name}
                            {' · '}
                          </span>
                        )}
                        <span>{movement.referenceCode || 'Sem referência'}</span>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className={`font-semibold ${colorClass}`}>
                        {movement.quantity} {movement.item.unit}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDateTime(movement.occurredAt)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold text-slate-700">
                <FaWarehouse className="text-indigo-500" /> Depósitos
              </h3>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openWarehouseModal(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                >
                  <FaPlus /> Novo depósito
                </button>
              )}
            </div>
            <ul className="space-y-3 text-sm">
              {warehouses.length === 0 ? (
                <li className="text-slate-500">Nenhum depósito cadastrado.</li>
              ) : (
                warehouses.map((warehouse) => (
                  <li
                    key={warehouse.id ?? warehouse.name}
                    className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <div>
                      <p className="font-semibold text-slate-700">
                        {warehouse.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {warehouse.code ? `Código: ${warehouse.code} · ` : ''}
                        {warehouse.address || 'Sem endereço informado'}
                      </p>
                      {warehouse.manager && (
                        <p className="text-xs text-slate-400">
                          Responsável: {warehouse.manager}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => openWarehouseModal(warehouse)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteWarehouse(warehouse)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 font-medium text-rose-600 transition hover:bg-rose-50"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-700">Categorias</h3>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openCategoryModal(null)}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                >
                  <FaPlus /> Nova categoria
                </button>
              )}
            </div>
            <ul className="space-y-3 text-sm">
              {categories.length === 0 ? (
                <li className="text-slate-500">Nenhuma categoria cadastrada.</li>
              ) : (
                categories.map((category) => (
                  <li
                    key={category.id ?? category.name}
                    className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 p-3"
                  >
                    <div>
                      <p className="font-semibold text-slate-700">
                        {category.name}
                      </p>
                      {category.description && (
                        <p className="text-xs text-slate-500">
                          {category.description}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-col gap-2 text-xs">
                        <button
                          type="button"
                          onClick={() => openCategoryModal(category)}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 font-medium text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
                        >
                          <FaEdit /> Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCategory(category)}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 font-medium text-rose-600 transition hover:bg-rose-50"
                        >
                          <FaTrash /> Excluir
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>

      <Modal
        open={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        size="max-w-3xl"
        showCloseButton={false}
        panelClassName="inventory-item-modal-panel"
      >
        <form className="inventory-item-form space-y-6" onSubmit={handleSaveItem}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {itemForm.id ? 'Editar item' : 'Novo item'}
            </h3>
            <button
              type="button"
              onClick={() => setItemModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Nome do produto
              <input
                type="text"
                required
                value={itemForm.name}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              SKU / Código interno
              <input
                type="text"
                value={itemForm.sku}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, sku: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Categoria
              <select
                value={itemForm.categoryId}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    categoryId: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="">Selecione</option>
                {categories.map((category) => (
                  <option
                    key={category.id ?? category.name}
                    value={category.id ?? category.name}
                  >
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Depósito padrão
              <select
                value={itemForm.warehouseId}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    warehouseId: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="">Selecione</option>
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse.id ?? warehouse.name}
                    value={warehouse.id ?? warehouse.name}
                  >
                    {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Unidade de medida
              <input
                type="text"
                value={itemForm.unit}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, unit: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Custo unitário (R$)
              <input
                type="number"
                step="0.01"
                min="0"
                value={itemForm.costPrice}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, costPrice: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Preço de venda (R$)
              <input
                type="number"
                step="0.01"
                min="0"
                value={itemForm.salePrice}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, salePrice: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Estoque mínimo
              <input
                type="number"
                min="0"
                value={itemForm.minimumStock}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    minimumStock: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Estoque máximo
              <input
                type="number"
                min="0"
                value={itemForm.maximumStock}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    maximumStock: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Estoque de segurança
              <input
                type="number"
                min="0"
                value={itemForm.safetyStock}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    safetyStock: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Validade
              <input
                type="date"
                value={itemForm.expirationDate}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    expirationDate: event.target.value,
                  }))
                }
                disabled={!itemForm.trackExpiration}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Descrição
            <textarea
              value={itemForm.description}
              onChange={(event) =>
                setItemForm((prev) => ({ ...prev, description: event.target.value }))
              }
              rows={3}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={itemForm.trackBatch}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    trackBatch: event.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Controlar lote
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={itemForm.trackExpiration}
                onChange={(event) =>
                  setItemForm((prev) => ({
                    ...prev,
                    trackExpiration: event.target.checked,
                    expirationDate: event.target.checked
                      ? prev.expirationDate
                      : '',
                  }))
                }
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Controlar validade
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={itemForm.active}
                onChange={(event) =>
                  setItemForm((prev) => ({ ...prev, active: event.target.checked }))
                }
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Item ativo
            </label>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setItemModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingItem}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingItem ? 'Salvando...' : 'Salvar item'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={movementModalOpen}
        onClose={() => setMovementModalOpen(false)}
        size="max-w-2xl"
        showCloseButton={false}
      >
        <form className="space-y-6" onSubmit={handleSaveMovement}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              Registrar movimentação
            </h3>
            <button
              type="button"
              onClick={() => setMovementModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Item
              <select
                required
                value={movementForm.itemId}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, itemId: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                <option value="">Selecione um item</option>
                {items.map((item) => (
                  <option key={item.id ?? item.sku} value={item.id ?? item.sku}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Tipo de movimentação
              <select
                value={movementForm.type}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, type: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              >
                {MOVEMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            {movementForm.type !== 'INBOUND' && (
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                Depósito de origem
                <select
                  value={movementForm.originWarehouseId}
                  onChange={(event) =>
                    setMovementForm((prev) => ({
                      ...prev,
                      originWarehouseId: event.target.value,
                    }))
                  }
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
                >
                  <option value="">Selecione</option>
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id ?? warehouse.name}
                      value={warehouse.id ?? warehouse.name}
                    >
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {movementForm.type !== 'OUTBOUND' && (
              <label className="flex flex-col gap-1 text-sm text-slate-600">
                Depósito de destino
                <select
                  value={movementForm.targetWarehouseId}
                  onChange={(event) =>
                    setMovementForm((prev) => ({
                      ...prev,
                      targetWarehouseId: event.target.value,
                    }))
                  }
                  className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
                >
                  <option value="">Selecione</option>
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id ?? warehouse.name}
                      value={warehouse.id ?? warehouse.name}
                    >
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {selectedMovementItem?.warehouses?.length ? (
              <div className="md:col-span-2 text-xs text-slate-500">
                <p className="font-medium text-slate-600">Disponível por depósito</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedMovementItem.warehouses.map((entry) => (
                    <span
                      key={`${selectedMovementItem.id}-${entry.id ?? entry.name}`}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"
                    >
                      <span className="font-medium">
                        {entry.name || 'Sem depósito'}
                      </span>
                      <span className="text-slate-500">
                        {Number(entry.quantity || 0)} {selectedMovementItem.unit}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Quantidade
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={movementForm.quantity}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, quantity: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Custo unitário (R$)
              <input
                type="number"
                min="0"
                step="0.01"
                value={movementForm.unitCost}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, unitCost: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Data da movimentação
              <input
                type="datetime-local"
                value={movementForm.occurredAt}
                onChange={(event) =>
                  setMovementForm((prev) => ({ ...prev, occurredAt: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Referência (NF, pedido, etc.)
              <input
                type="text"
                value={movementForm.referenceCode}
                onChange={(event) =>
                  setMovementForm((prev) => ({
                    ...prev,
                    referenceCode: event.target.value,
                  }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Observações
            <textarea
              value={movementForm.notes}
              onChange={(event) =>
                setMovementForm((prev) => ({ ...prev, notes: event.target.value }))
              }
              rows={3}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setMovementModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingMovement}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingMovement ? 'Registrando...' : 'Registrar movimentação'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={warehouseModalOpen}
        onClose={() => setWarehouseModalOpen(false)}
        size="max-w-xl"
        showCloseButton={false}
      >
        <form className="space-y-6" onSubmit={handleSaveWarehouse}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {warehouseForm.id ? 'Editar depósito' : 'Novo depósito'}
            </h3>
            <button
              type="button"
              onClick={() => setWarehouseModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Nome do depósito
              <input
                type="text"
                required
                value={warehouseForm.name}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, name: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Código interno
              <input
                type="text"
                value={warehouseForm.code}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, code: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Responsável
              <input
                type="text"
                value={warehouseForm.manager}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, manager: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              Telefone
              <input
                type="tel"
                value={warehouseForm.phone}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, phone: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600">
              E-mail
              <input
                type="email"
                value={warehouseForm.email}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, email: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Endereço
              <input
                type="text"
                value={warehouseForm.address}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, address: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
              Observações
              <textarea
                rows={3}
                value={warehouseForm.notes}
                onChange={(event) =>
                  setWarehouseForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
              />
            </label>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setWarehouseModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingWarehouse}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingWarehouse ? 'Salvando...' : 'Salvar depósito'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        size="max-w-md"
        showCloseButton={false}
      >
        <form className="space-y-6" onSubmit={handleSaveCategory}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">
              {categoryForm.id ? 'Editar categoria' : 'Nova categoria'}
            </h3>
            <button
              type="button"
              onClick={() => setCategoryModalOpen(false)}
              className="text-slate-400 transition hover:text-slate-600"
              aria-label="Fechar"
            >
              <FaTimes />
            </button>
          </div>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Nome da categoria
            <input
              type="text"
              required
              value={categoryForm.name}
              onChange={(event) =>
                setCategoryForm((prev) => ({ ...prev, name: event.target.value }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-slate-600">
            Descrição
            <textarea
              rows={3}
              value={categoryForm.description}
              onChange={(event) =>
                setCategoryForm((prev) => ({ ...prev, description: event.target.value }))
              }
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-300 focus:outline-none"
            />
          </label>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setCategoryModalOpen(false)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingCategory}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {savingCategory ? 'Salvando...' : 'Salvar categoria'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
